import React, { type FC } from 'react'
import { Box, Grid, Paper, type SxProps, type Theme, Typography, typographyClasses } from '@mui/material'

import { areArrayEquals, isArrayNotEmpty } from '../../../../utils'

import { selectExplorerMonitoredTopicsMessage, selectExplorerMonitoredTopicsToUnsubcribe, Translation } from '../../../../domain'

import { BufferDisplay, ReversedTruncatedText, SmallButton } from '../../common'
import { SmallCopy } from '../../Copy'
import { FormattedTranslation } from '../../Internationalization'
import { useAppState, useAppUsecase } from '../../State'
import { usePagination } from '../Pagination'

const topicValueStyle: SxProps<Theme> = { '& > :first-of-type': { overflowX: 'hidden', textOverflow: 'ellipsis', pr: 2 } }

const Display: FC<{ topic: string }> = ({ topic }) => {
    const formatting = useAppUsecase('explorerTopicsFormattingUsecase')

    const message = useAppState((s) => selectExplorerMonitoredTopicsMessage(s, topic), [topic])

    if (message === null) {
        return <FormattedTranslation id={Translation.NO_MESSAGES} />
    }

    return (
        <BufferDisplay
            sx={topicValueStyle}
            className={`topic-payload-${message.selectedType.toLowerCase()}`}
            fullWidth
            buffer={message.payload}
            types={message.possibleTypes}
            onChange={(type) => formatting.selectLatestValueType(topic, type)}
        />
    )
}

const Unsub: FC<{ topic: string }> = ({ topic }) => {
    const manage = useAppUsecase('explorerTopicManagementUsecase')
    const topicsToUnsub = useAppState((s) => selectExplorerMonitoredTopicsToUnsubcribe(s, topic), [topic], areArrayEquals)

    return isArrayNotEmpty(topicsToUnsub) ? (
        <SmallButton className="topic-unsub-button" onClick={() => manage.toggleTopics(topicsToUnsub)}>
            <FormattedTranslation id={Translation.UNSUB} />
        </SmallButton>
    ) : null
}

const noMessagesStyle: SxProps<Theme> = { p: 2 }
const wrapperStyle: SxProps<Theme> = { mb: 2, py: 1, px: 0 }
const valueWrapperStyle: SxProps<Theme> = { pr: 2, my: 'auto' }
const topicWrapperStyle: SxProps<Theme> = {
    display: 'flex',
    my: 'auto',
    px: 2,
    '& > *': { my: 'auto' },
    [`& > *:not(.${typographyClasses.root})`]: {
        maxWidth: 0,
        opacity: 0,
        overflow: 'hidden',
        transition: ({ transitions: { easing } }) => ['max-width', 'opacity'].map((p) => `${p} 100ms ${easing.easeInOut}`).join(', '),
    },
    [`&:hover > *:not(.${typographyClasses.root})`]: { maxWidth: 64, opacity: 1, overflow: 'initial' },
}
const topicStyle: SxProps<Theme> = { overflowX: 'hidden', mr: 'auto', display: 'flex', alignItems: 'center' }
const topicActionStyle: SxProps<Theme> = { pl: 1 }
const copyStyle: SxProps<Theme> = { lineHeight: 1 }

export const TableLines: FC<Readonly<{ topics: readonly string[] }>> = ({ topics }) => {
    /**
     * Why was this magical number chosen? Vibes
     */
    const [loaded, button] = usePagination(topics, 25)

    if (!isArrayNotEmpty(loaded)) {
        return (
            <Typography sx={noMessagesStyle} className="no-messages" variant="body2" align="center">
                <FormattedTranslation id={Translation.NO_MESSAGES} />
            </Typography>
        )
    }

    return (
        <>
            {loaded.map((topic) => (
                <Paper key={topic} sx={wrapperStyle} variant="outlined">
                    <Grid container>
                        <Grid sx={topicWrapperStyle} title={topic} size={{ xs: 5 }}>
                            <Typography sx={topicStyle} className="topic-latest-label" variant="body2">
                                <ReversedTruncatedText>{topic}</ReversedTruncatedText>
                            </Typography>
                            <Box sx={topicActionStyle}>
                                <Unsub topic={topic} />
                            </Box>
                            <Box sx={topicActionStyle}>
                                <SmallCopy sx={copyStyle}>{topic}</SmallCopy>
                            </Box>
                        </Grid>
                        <Grid className="topic-latest-value" size={{ xs: 7 }} sx={valueWrapperStyle}>
                            <Display topic={topic} />
                        </Grid>
                    </Grid>
                </Paper>
            ))}
            {button}
        </>
    )
}
